import { Box, useToken } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { IoEyeOutline } from 'react-icons/io5';
import { MdEdit } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import Show from '@/components/Molecules/Show';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import {
	useGetSupportTicket,
	useGetSupportTickets,
} from '@/hooks/service-hooks/supportTickets';
import usePermission from '@/hooks/usePermission';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import { getSupportTickets } from '@/services/supportTickets';
import type { SupportTicketRaw } from '@/types/supportTicket';
import type { FilterType } from '@/types/table';
import { formatDate } from '@/utils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import {
	calculateDaysOpen,
	mapPriority,
	mapStatus,
	statusFilterOptions,
} from '../constants';
import { SupportTicketDetails } from '../SupportTicketDetails';
import { SupportTicketForm } from '../SupportTicketForm';
import { columns } from './columns';
import { buildDocumentsUrl } from '@/utils/navigationItems';
import { IoMdDocument } from 'react-icons/io';

const CLINIC_KEY = '_customerid_value';

const SupportTicketsTable = () => {
	const [brand600] = useToken('colors', ['brand.600']);
	const { hasPermission } = usePermission();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	// Shared clinic filter helpers
	const { defaultClinicIds, mapGlobalFilters } = useClinicFilter(CLINIC_KEY);

	// Initialize with DataTable's default values and clinic filters from store
	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: defaultClinicIds.length > 0,
		status: '',
		globalFilters: JSON.stringify(
			defaultClinicIds.length
				? [
						{
							key: CLINIC_KEY,
							values: defaultClinicIds,
						},
					]
				: [],
		),
		sort: '',
	});

	// Modal state management
	const [modalInformation, setModalInformation] = useState<{
		mode: 'create' | 'edit' | 'details' | null;
		data: string | null;
	}>({
		mode: null,
		data: null,
	});

	// Track form dirty state for exit confirmation
	const [isFormDirty, setIsFormDirty] = useState(false);
	const [supportTicketExitDraftAction, setSupportTicketExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);

	const { data, isFetching, refetch } = useGetSupportTickets(filters);

	// Get individual ticket data for details modal
	const { data: ticketDetails, isFetching: isTicketDetailsLoading } =
		useGetSupportTicket(
			modalInformation.mode === 'details'
				? (modalInformation.data as string)
				: null,
			modalInformation.mode === 'details',
		);

	// Transform API data to table format
	const transformedData = useMemo(() => {
		if (!data?.items) return [];

		return data.items.map((item) => {
			const priority = mapPriority(item.prioritycode);
			const status = mapStatus(item.statuscode);

			return {
				id: item.incidentid,
				title: (
					<FaceliftTruncatedTextWithTooltip
						text={item.title || '-'}
						maxLength={25}
					/>
				),
				ticketnumber: (
					<FaceliftTruncatedTextWithTooltip
						text={item.ticketnumber || '-'}
						maxLength={20}
					/>
				),
				description: (
					<FaceliftTruncatedTextWithTooltip
						text={item.description || '-'}
						maxLength={60}
					/>
				),
				createdon: formatDate(item.createdon), // Formatted date for display, backend uses raw value for sorting/filtering
				daysOpen: calculateDaysOpen(item.createdon), // Calculated days for display
				prioritycode: (
					<Box
						bg={priority.color}
						borderRadius={4}
						color="white"
						height="24px"
						px={3}
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight={500}
						fontSize="sm"
					>
						{priority.label}
					</Box>
				),
				statuscode: (
					<Box
						bg={status.color}
						borderRadius={4}
						color="white"
						height="24px"
						px={3}
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight={500}
						fontSize="sm"
					>
						<FaceliftTruncatedTextWithTooltip
							text={status.label}
							maxLength={20}
						/>
					</Box>
				),
				ta_documentupload: item.ta_documentupload,
				_customerid_value: item._customerid_value,
				customerName: item.customer?.name || '',
			};
		});
	}, [data]);

	const transformedDataToExport = (data: SupportTicketRaw[]) => {
		if (!data) return [];

		return data.map((item) => {
			return {
				id: item.incidentid,
				title: item.title || '-',
				ticketnumber: item.ticketnumber || '-',
				description: item.description || '-',
				createdon: formatDate(item.createdon),
				daysOpen: calculateDaysOpen(item.createdon) || '0',
				prioritycode: mapPriority(item.prioritycode).label,
				statuscode: mapStatus(item.statuscode).label,
			};
		});
	};

	// Handle filter apply
	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
	}: onFilterApplyProps) => {
		// Transform daysOpen filters to createdon date filters
		const transformedColumnsFilters = columnsFilters.map((filter) => {
			if (filter.key === 'daysOpen') {
				// Convert days to date range
				const days = Number(filter.value);
				if (!isNaN(days)) {
					const targetDate = new Date();
					targetDate.setDate(targetDate.getDate() - days);

					// Format as YYYY-MM-DD
					const dateStr = targetDate.toISOString().split('T')[0];

					return {
						...filter,
						key: 'createdon', // Map to backend field
						type: 'date',
						value: `${dateStr}T00:00:00Z || ${dateStr}T23:59:59Z`,
					};
				}
			}
			return filter;
		});

		// Map global filters (handles clinic selections)
		const mappedFilters = mapGlobalFilters(globalFilters || []);

		const statusValue = status?.value?.toString() || '';

		setFilters({
			page,
			limit,
			columnsFilters: JSON.stringify(transformedColumnsFilters),
			search,
			enabled: defaultClinicIds.length > 0,
			status: statusValue,
			globalFilters: JSON.stringify(mappedFilters),
			sort: sort ? `${sort.key}|${sort.direction}` : '',
		});
	};

	// Handle create ticket
	const handleCreateTicket = () => {
		setIsFormDirty(false);
		setModalInformation({
			mode: 'create',
			data: null,
		});
	};

	// Handle view ticket details
	const handleViewDetails = (ticketId: string) => {
		setModalInformation({
			mode: 'details',
			data: ticketId,
		});
	};

	// Handle edit ticket - fetch full details first
	const handleEditTicket = async (ticketId: string) => {
		// We need to fetch full ticket details to get all information including clinic
		setIsFormDirty(false);
		setModalInformation({
			mode: 'edit',
			data: ticketId, // Pass ID will fetch in form
		});
	};

	const handleCloseModal = () => {
		setModalInformation({ mode: null, data: null });
		setIsFormDirty(false);
	};

	const handleFormSuccess = () => {
		setModalInformation({ mode: null, data: null });
		setIsFormDirty(false);
		refetch();
	};

	const exportAllAsCSV = async () => {
		try {
			const data = await getSupportTickets(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(data?.items || []);
			generateCSVFromTable(
				columns,
				transformedData,
				`SupportTickets-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	const exportAllAsExcel = async () => {
		try {
			const data = await getSupportTickets(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(data?.items || []);
			generateExcelFromTable(
				columns,
				transformedData,
				`SupportTickets-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	const navigateToDoc = (documentUrl: string, customerId: string, customerName: string) => {
		if (documentUrl && customerId && customerName) {
			return buildDocumentsUrl(
				documentUrl,
				customerId,
				customerName,
			) || undefined;
		}
		return undefined;
	};

	return (
		<>
			<Box
				p={{ base: 4, md: 8 }}
				py={{ base: 4, md: 6 }}
				bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			>
			<DataTable
				tableName="SupportTickets"
				moduleName="Support Ticket"
				loading={isFetching}
					items={transformedData}
					columns={columns}
					topLabel="Support Tickets"
					totalCount={data?.meta.totalRecords || 0}
					onFilterApply={onFilterApply}
					expandable
					fullscreen
					paginated
					hideShowColumn
					exportable
					excel
					csv
					exportAllAsCSV={exportAllAsCSV}
					exportAllAsExcel={exportAllAsExcel}
					customExportItemsData={transformedDataToExport(data?.items || [])}
					searchable
					bordered
					globalFilter={{
						title: 'Filters',
						clinics: true,
					}}
			{...(hasPermission(['SupportTickets:Create']) && {
				creatableFn: handleCreateTicket,
			})}
			statusFilters={statusFilterOptions}
			actions={[
						{
							label: 'Edit',
							icon: <MdEdit />,
							actionFn: (item: ColumnData) => {
								const ticketId = item.id as string;
								if (ticketId) {
									handleEditTicket(ticketId);
								}
							},
							show: () => hasPermission(['SupportTickets:Edit']),
						},
						{
							label: 'Details',
							icon: <IoEyeOutline />,
							actionFn: (item: ColumnData) => {
								const ticketId = item.id as string;
								if (ticketId) {
									handleViewDetails(ticketId);
								}
							},
							show: () => hasPermission(['SupportTickets:Visualize']),
						},
						// {
						// 	label: 'View Document',
						// 	icon: <IoMdDocument />,
						// 	show: (item: ColumnData) => !!item.ta_documentupload,
						// 	actionFn: (item: ColumnData) => {
						// 		const url =navigateToDoc(item.ta_documentupload as string, item._customerid_value as string, item.customerName as string);
						// 		if (url) {
						// 			navigate(url);
						// 		}
						// 	},
						// },
					]}
				/>
			</Box>

			<FaceliftDialogWithExitConfirmation
				showBackBtn={true}
				open={
					modalInformation.mode === 'create' || modalInformation.mode === 'edit'
				}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				title={
					modalInformation.mode === 'create'
						? 'Create New Ticket'
						: 'Edit Ticket'
				}
				size="xl"
				sx={{
					width: '850px',
					maxWidth: '95vw',
					maxHeight: '95vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowX: 'hidden',
					overflowY: 'auto',
				}}
				isDirty={isFormDirty}
				saveDraftAction={supportTicketExitDraftAction}
			>
				<SupportTicketForm
					mode={modalInformation.mode as 'create' | 'edit'}
					ticketId={
						modalInformation.mode === 'edit'
							? (modalInformation.data as string)
							: undefined
					}
					onSuccess={handleFormSuccess}
					onIsDirtyChange={setIsFormDirty}
					onExitConfirmationDraftActionChange={
						modalInformation.mode === 'create' || modalInformation.mode === 'edit'
							? setSupportTicketExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>

			<FaceliftDialog
				showBackBtn={true}
				title="Details"
				open={modalInformation.mode === 'details'}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				sx={{
					width: '800px',
					maxWidth: '95vw',
					maxHeight: '95vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowY: 'auto',
				}}
			>
				<Show when={isTicketDetailsLoading}>
					<FaceliftDataLoader
						title="Loading ticket details..."
						description="Please wait while we fetch the ticket information."
					/>
				</Show>
				<Show when={!isTicketDetailsLoading}>
					<SupportTicketDetails data={ticketDetails || null} />
				</Show>
			</FaceliftDialog>
		</>
	);
};

export default SupportTicketsTable;
